import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PostureCamera } from './PostureCamera';
import { PostureFeedback } from './PostureFeedback';
import { Camera, CameraOff, Volume2, VolumeX } from 'lucide-react';
import { PostureIssue, PostureMetrics } from '@/hooks/usePostureDetection';

interface PostureAnalysisProps {
  isRunning: boolean;
  onDataUpdate?: (data: { metrics: PostureMetrics | null; issues: PostureIssue[] }) => void;
}

export const PostureAnalysis = ({ isRunning, onDataUpdate }: PostureAnalysisProps) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [enableVoice, setEnableVoice] = useState(true);
  const [currentMetrics, setCurrentMetrics] = useState<PostureMetrics | null>(null);
  const [currentIssues, setCurrentIssues] = useState<PostureIssue[]>([]);

  const handleToggle = () => {
    if (!isRunning) return;
    setIsEnabled(!isEnabled);
  };

  const handleMetricsUpdate = (metrics: PostureMetrics) => {
    setCurrentMetrics(metrics);
    if (onDataUpdate) {
      onDataUpdate({ metrics, issues: currentIssues });
    }
  };

  const handleIssuesUpdate = (issues: PostureIssue[]) => {
    setCurrentIssues(issues);
    if (onDataUpdate) {
      onDataUpdate({ metrics: currentMetrics, issues });
    }
  };

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="h-5 w-5" />
          <h3 className="font-semibold">Análise de Postura com IA</h3>
        </div>
        
        <div className="flex items-center gap-2">
          <Switch
            id="posture-enabled"
            checked={isEnabled}
            onCheckedChange={handleToggle}
            disabled={!isRunning}
          />
          <Label htmlFor="posture-enabled" className="text-sm cursor-pointer">
            {isEnabled ? <Camera className="h-4 w-4 text-green-500" /> : <CameraOff className="h-4 w-4" />}
          </Label>
        </div>
      </div>

      {isEnabled && (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-2 bg-muted rounded-lg">
            <Label htmlFor="voice-feedback" className="text-sm">Feedback por Voz</Label>
            <div className="flex items-center gap-2">
              <Switch
                id="voice-feedback"
                checked={enableVoice}
                onCheckedChange={setEnableVoice}
              />
              {enableVoice ? (
                <Volume2 className="h-4 w-4 text-primary" />
              ) : (
                <VolumeX className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>

          <PostureCamera
            isActive={isEnabled}
            onMetricsUpdate={handleMetricsUpdate}
            onIssuesUpdate={handleIssuesUpdate}
          />

          {currentMetrics && (
            <PostureFeedback
              issues={currentIssues}
              overallScore={currentMetrics.overallScore}
              enableVoice={enableVoice}
            />
          )}
        </div>
      )}

      {!isRunning && (
        <p className="text-xs text-muted-foreground text-center">
          Inicie uma corrida para ativar a análise de postura
        </p>
      )}

      {isEnabled && (
        <div className="text-xs text-muted-foreground space-y-1">
          <p>💡 <strong>Dica:</strong> Posicione seu celular em um suporte ou braçadeira para capturar todo o corpo.</p>
          <p>🔋 A análise de postura consome mais bateria. Desative se necessário.</p>
        </div>
      )}
    </Card>
  );
};
